import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Check, MicOff, Plus } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { GoalsResponseDataSchema } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<Features, z.infer<typeof GoalsResponseDataSchema>[number]>()

type Props = {
  goals: z.infer<typeof GoalsResponseDataSchema> | undefined
  isPending: boolean
  onSelectGoal: (goal: z.infer<typeof GoalsResponseDataSchema>[number]) => void
  selectedGoalIds: string[]
  sortField: "name" | "type" | "createdAt"
  sortOrder: "asc" | "desc"
  onSort: (field: "name" | "type" | "createdAt") => void
}

export function GoalPickerTable({
  goals,
  isPending,
  onSelectGoal,
  selectedGoalIds,
  sortField,
  sortOrder,
  onSort,
}: Props) {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("campaigns.goalsManagement.table.name"),
          cell: (props) => {
            const goal = props.row.original
            const name = goal.displayName
            const isUnderlying = goal.isUnderlying

            return (
              <div className="flex flex-row items-center justify-start gap-2">
                <div className="w-4 relative flex items-center justify-center">
                  {isUnderlying && (
                    <Tooltip>
                      <TooltipTrigger>
                        <MicOff className="size-4" />
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        {t("campaigns.goals.underlyingDescription")}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="max-w-full overflow-hidden flex-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={cn("truncate block", !name && "text-muted-foreground")}>
                        {name ?? t("campaigns.untitled")}
                      </span>
                    </TooltipTrigger>
                    {name && (
                      <TooltipContent>
                        <p className="max-w-xs break-words">{name}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            )
          },
          meta: { className: "w-3/8" },
        }),
        columnHelper.accessor("assistantInstructions", {
          header: t("campaigns.goalsManagement.table.description"),
          cell: (props) => {
            const description = props.getValue()
            return (
              <div className="max-w-full overflow-hidden">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={cn("truncate block", !description && "text-muted-foreground")}>
                      {description || "-"}
                    </span>
                  </TooltipTrigger>
                  {description && (
                    <TooltipContent>
                      <p className="max-w-xs break-words">{description}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            )
          },
          meta: { className: "w-3/8" },
        }),
        columnHelper.accessor("type", {
          header: t("campaigns.goalsManagement.table.type"),
          cell: (props) => {
            const goal = props.row.original
            return (
              <Badge variant="secondary" key="trigger-value">
                {goal.type}
              </Badge>
            )
          },
          meta: { className: "w-1/8", align: "center" },
        }),
        columnHelper.accessor("id", {
          header: "",
          cell: (props) => {
            const goal = props.row.original
            const isAdded = selectedGoalIds.includes(goal.id)
            return (
              <Button
                variant={isAdded ? "secondary" : "outline"}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isAdded) {
                    onSelectGoal(goal)
                  }
                }}
                disabled={isAdded}
              >
                {isAdded ? (
                  <>
                    <Check className="size-4" />
                    {t("campaigns.goals.added")}
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    {t("campaigns.goals.add")}
                  </>
                )}
              </Button>
            )
          },
          meta: { className: "w-1/8", align: "right" },
        }),
      ]),
    [t, onSelectGoal, selectedGoalIds],
  )

  const table = useTable({
    data: goals ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || goals === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (goals.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.goalsManagement.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id} className="h-16">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  const buildSortable = (headerId: string) => {
    const sortFields = ["name", "type", "createdAt"]
    if (!sortFields.includes(headerId)) return

    const isActive = sortField === headerId

    return {
      active: isActive,
      direction: isActive ? sortOrder : "asc",
      onClick: () => onSort(headerId as "name" | "type" | "createdAt"),
    }
  }

  return (
    <Table className="table-fixed">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header.id)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
